import { notifySaveSuccess } from '../utils/saveNotification';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, BlockStack, Button, Card, InlineStack, Modal, Select, Text, TextField } from './admin-ui';
import { settingsError, settingsRequest } from '../api/adminSettings';
import ajax from '../api/ajax';
import { API_DOMAIN } from '../utils/constants';
import { SecretField, SettingsFeedback, SettingsLoading } from './SettingsFields';

const senderDraft = setting => ({ mailer: setting?.mailer || 'smtp', from_name: setting?.from_name || '', from_address: setting?.from_address || '', host: setting?.host || '', port: setting?.port || '587', username: setting?.username || '', password: '', encryption: setting?.id ? setting.encryption || 'none' : 'tls' });

export default function EmailSenderSettings({ onStatus }) {
  const [saved, setSaved] = useState(null);
  const [delivery, setDelivery] = useState({ delay: '3', is_active: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const original = useRef('');
  const [modalBusy, setModalBusy] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const popup = useRef(null);
  const [linking, setLinking] = useState(false);
  const updateSaved = useCallback(data => { setSaved(data); onStatus(data.id ? Boolean(data.is_active) : false); }, [onStatus]);
  const load = useCallback(async () => {
    setError('');
    try {
      const data = await settingsRequest('g-email-setting');
      updateSaved(data);
      setDelivery({ delay: String(data.delay ?? 3), is_active: Boolean(data.is_active) });
    } catch (e) { setError(settingsError(e)); }
  }, [updateSaved]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!linking) return undefined;
    let refreshing = false;
    const refresh = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const data = await settingsRequest('g-email-setting');
        updateSaved(data);
        if (data.outlook_linked || popup.current?.closed) {
          setLinking(false);
          setModalSuccess(data.outlook_linked ? 'Outlook authorization saved.' : 'Authorization is not complete. You can link Outlook again.');
        }
      } catch (e) { setModalError(settingsError(e)); setLinking(false); }
      finally { refreshing = false; }
    };
    const focus = () => { refresh(); };
    const timer = setInterval(() => { if (popup.current?.closed) refresh(); }, 1000);
    window.addEventListener('focus', focus);
    return () => { clearInterval(timer); window.removeEventListener('focus', focus); };
  }, [linking, updateSaved]);

  const configure = () => {
    const value = senderDraft(saved);
    setDraft(value); original.current = JSON.stringify(value);
    setModalError(''); setModalSuccess(''); setOpen(true);
  };
  const dirty = draft && JSON.stringify(draft) !== original.current;
  const close = () => {
    if (modalBusy || (dirty && !window.confirm('Discard unsaved sender changes?'))) return;
    setOpen(false); setDraft(null);
  };
  const saveDelivery = async () => {
    if (!/^\d+$/.test(delivery.delay) || Number(delivery.delay) > 255) { setError('Enter a sending delay from 0 to 255 minutes.'); return; }
    setBusy(true); setError(''); setSuccess('');
    try {
      const data = await settingsRequest('c-u-emailSetting', { scope: 'delivery', id: saved.id, delay: Number(delivery.delay), is_active: delivery.is_active });
      updateSaved(data); setSuccess('Sending settings saved.'); notifySaveSuccess();
    } catch (e) { setError(settingsError(e)); }
    finally { setBusy(false); }
  };
  const saveSender = async () => {
    setModalBusy(true); setModalError(''); setModalSuccess('');
    try {
      const input = draft.mailer === 'smtp' ? draft : { mailer: 'outlook', from_address: draft.from_address };
      const data = await settingsRequest('c-u-emailSetting', { scope: 'sender', ...(saved.id ? { id: saved.id } : {}), ...input });
      updateSaved(data);
      const value = senderDraft(data);
      setDraft(value); original.current = JSON.stringify(value);
      setModalSuccess('Sender saved.'); notifySaveSuccess();
      if (draft.mailer === 'smtp') { setOpen(false); setSuccess('Sender saved.'); }
    } catch (e) { setModalError(settingsError(e)); }
    finally { setModalBusy(false); }
  };
  const linkOutlook = async () => {
    setModalError(''); setModalSuccess('');
    const child = window.open('about:blank', '_blank');
    if (!child) { setModalError('Allow pop-ups to link your Outlook account.'); return; }
    child.opener = null;
    popup.current = child;
    setModalBusy(true);
    try {
      const response = await ajax(`${API_DOMAIN}/api/a/setting-outlook`, {}, 'GET');
      if (response.status !== 200 || !response.data?.auth_url) throw new Error(response.msg || 'Unable to start Outlook authorization.');
      const url = new URL(response.data.auth_url);
      if (url.protocol !== 'https:' || url.hostname !== 'login.microsoftonline.com') throw new Error('Unexpected Outlook authorization address.');
      child.location.href = url.href;
      setLinking(true);
    } catch (e) { child.close(); setModalError(settingsError(e)); }
    finally { setModalBusy(false); }
  };
  const field = (key, label, options = {}) => <TextField label={label} value={draft[key]} onChange={value => setDraft(prev => ({ ...prev, [key]: value }))} disabled={modalBusy || linking} autoComplete="off" {...options} />;
  const changeDelivery = (key, value) => { setDelivery(prev => ({ ...prev, [key]: value })); setSuccess(''); };
  const providerKey = event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || modalBusy || linking) return;
    event.preventDefault();
    const mailer = event.key === 'Home' ? 'smtp' : event.key === 'End' ? 'outlook' : draft.mailer === 'smtp' ? 'outlook' : 'smtp';
    setDraft(prev => ({ ...prev, mailer }));
    event.currentTarget.parentElement.querySelectorAll('button')[mailer === 'smtp' ? 0 : 1].focus();
    setModalError(''); setModalSuccess('');
  };

  return <Card><BlockStack gap="400">
    <InlineStack gap="200" blockAlign="center"><Text as="h2" variant="headingMd">Sending settings</Text>{saved?.id && <Badge>{saved.mailer === 'outlook' ? 'Outlook' : 'SMTP'}</Badge>}</InlineStack>
    <SettingsFeedback error={error} success={success} retry={!saved ? load : undefined} />
    {!saved ? (!error && <SettingsLoading />) : <>
      <div className="email-sender-summary"><Text as="p" fontWeight="semibold">{saved.id ? saved.from_name || 'Sender account' : 'No sender configured'}</Text>{saved.id && <Text as="p" tone="subdued">{saved.from_address}</Text>}</div>
      <InlineStack align="space-between" blockAlign="center"><Text as="h3" variant="headingSm">Sender account</Text><Button onClick={configure} disabled={busy}>Configure</Button></InlineStack>
      <TextField label="Sending delay" type="number" min={0} max={255} value={delivery.delay} suffix="minutes" onChange={value => changeDelivery('delay', value)} autoComplete="off" disabled={busy} helpText="Delay used by the existing email schedule." />
      <div className="email-switch-row"><button className="email-switch" type="button" role="switch" aria-label="Enable sending" aria-checked={delivery.is_active} disabled={busy} onClick={() => changeDelivery('is_active', !delivery.is_active)}><span /></button><div><Text as="p">Enable sending</Text><Text as="p" tone="subdued">Turn on when your sender and content are ready.</Text></div></div>
      {!saved.id && <Text as="p" tone="subdued">Configure a sender account before saving.</Text>}
      <InlineStack align="end"><Button variant="primary" onClick={saveDelivery} loading={busy} disabled={!saved.id || modalBusy || linking}>Save settings</Button></InlineStack>
    </>}
    <Modal open={open} onClose={close} title="Configure sender"
      primaryAction={{ content: draft?.mailer === 'outlook' ? (saved?.outlook_linked ? 'Relink Outlook' : 'Link Outlook') : 'Save sender', loading: modalBusy, disabled: linking || (draft?.mailer === 'outlook' && (!saved?.id || saved.mailer !== 'outlook' || dirty)), onAction: draft?.mailer === 'outlook' ? linkOutlook : saveSender }}
      secondaryActions={[{ content: 'Cancel', disabled: modalBusy, onAction: close }, ...(draft?.mailer === 'outlook' ? [{ content: 'Save sender', disabled: modalBusy || linking, onAction: saveSender }] : [])]}>
      <Modal.Section>{draft && <BlockStack gap="400">
        <Text as="p" tone="subdued">Choose how your checkout emails are sent.</Text>
        <div className="email-provider-tabs" role="tablist" aria-label="Email provider">{['smtp', 'outlook'].map(mailer => <button key={mailer} role="tab" type="button" tabIndex={draft.mailer === mailer ? 0 : -1} aria-selected={draft.mailer === mailer} disabled={modalBusy || linking} onKeyDown={providerKey} onClick={() => { setDraft(prev => ({ ...prev, mailer })); setModalError(''); setModalSuccess(''); }}>{mailer === 'smtp' ? 'SMTP' : 'Outlook'}</button>)}</div>
        <SettingsFeedback error={modalError} success={modalSuccess} />
        {draft.mailer === 'smtp' ? <>
          <div className="email-field-grid">{field('from_name', 'From name')}{field('from_address', 'From address', { type: 'email' })}</div>
          <div className="email-field-grid email-host-grid">{field('host', 'SMTP host')}{field('port', 'Port', { type: 'number', min: 1, max: 65535 })}</div>
          {field('username', 'Username')}
          <SecretField label="Password" value={draft.password} disabled={modalBusy} onChange={password => setDraft(prev => ({ ...prev, password }))} helpText={saved?.has_password ? 'Leave blank to keep the saved password.' : 'Enter the password for this SMTP account.'} />
          <Select label="Encryption" options={[{ label: 'TLS', value: 'tls' }, { label: 'SSL', value: 'ssl' }, { label: 'None', value: 'none' }]} value={draft.encryption} onChange={encryption => setDraft(prev => ({ ...prev, encryption }))} disabled={modalBusy} />
          <div className="email-note">Saving credentials does not verify the connection.</div>
        </> : <>
          {field('from_address', 'Outlook email', { type: 'email' })}
          <InlineStack><Badge tone={saved?.outlook_linked && !dirty ? 'success' : undefined}>{saved?.outlook_linked && !dirty ? 'Authorization saved' : 'Not linked'}</Badge></InlineStack>
          <Text as="p" tone="subdued">Save the email address, then link your Microsoft account to authorize sending.</Text>
          <div className="email-note">{linking ? 'Complete authorization in the new window, then return here.' : 'Authorization opens in a new window.'}</div>
        </>}
      </BlockStack>}</Modal.Section>
    </Modal>
  </BlockStack></Card>;
}
